import {
  Autocomplete,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Validation, ValidationLevel } from '@/api/graphql';
import { dedupeTags } from './tags';

interface Props {
  label: string;
  /** `AdiProperties` key — also the key validations are reported against. */
  name: string;
  original?: string[] | null;
  /** The persisted override, if any. */
  editorial?: string[] | null;
  /** Uncommitted local edit, if the operator has changed something. */
  draft?: string[] | null;
  onChange: (name: string, values: string[]) => void;
  validations: Validation[];
  /**
   * The configured vocabulary, when the field has one. Absent for open lists like cast and crew, which
   * are free text by nature — there is no closed set of actors.
   *
   * `id` is the value stored and validated; `name` is only a label. They diverge more often than not —
   * a rating is `PG-13` named "PG-13 — Parents Strongly Cautioned", a device type is `APPLE_TV` named
   * "Apple TV" — and passing names as values put the label into the data, which then failed its own
   * vocabulary check.
   */
  options?: { id: string; name: string }[];
  /**
   * Renders a tag for display without changing it.
   *
   * Used for localities, where the provider sends operator codes like `llapa` that mean nothing on
   * screen. The chip can say "llapa · Panama" while `value` — and therefore the export — keeps the
   * provider's string. Distinct from the vocabulary match below, which maps a value to its curated
   * spelling; this maps it to an explanation.
   */
  labelFor?: (tag: string) => string;
  disabled?: boolean;
}

/**
 * A repeating metadata field — rating, genre, category, cast, crew — as editable chips.
 *
 * The scalar editor cannot serve these: joining a list into a comma-separated string and splitting
 * it back makes any value containing a comma unrepresentable, and cast names routinely contain one
 * ("Ingram,Terry" is how the provider sent it). Each entry is its own chip for that reason.
 *
 * `freeSolo` is deliberate even where a vocabulary exists. The provider's value is whatever the
 * provider sent; if it is off-vocabulary the validation rule should say so and the operator should
 * be able to see and correct it — an editor that silently refuses to display a non-conforming value
 * hides exactly the problem the tool exists to surface.
 */
export default function EditableListField({
  label,
  name,
  original,
  editorial,
  draft,
  onChange,
  validations,
  options,
  disabled,
  labelFor,
}: Props) {
  const fieldValidations = validations.filter((v) => v.field === name);
  const worst = fieldValidations.some((v) => v.level === ValidationLevel.Error)
    ? ValidationLevel.Error
    : fieldValidations.length
    ? ValidationLevel.Warning
    : undefined;

  const persisted = editorial ?? original ?? [];
  const value = draft ?? persisted;
  const hasOverride = editorial !== null && editorial !== undefined;

  const helper =
    fieldValidations.map((v) => v.message).join(' · ') ||
    (hasOverride && original?.length
      ? `Provider sent: ${original.join(', ')}`
      : ' ');

  /**
   * The vocabulary entry a stored value corresponds to, matched on `id`, ignoring case.
   *
   * Case-insensitive because providers send `DRAMA` where the vocabulary reads `Drama`; a case-sensitive
   * comparison marked every real NBCU genre as unrecognised while validation, which lowercases both
   * sides, considered it fine. The UI was the only thing calling it wrong.
   *
   * Display only — `value` keeps the provider's exact string, so an unedited field exports as it arrived.
   */
  const vocabularyMatch = (tag: string) =>
    options?.find((option) => option.id.toLowerCase() === tag.toLowerCase());

  return (
    <Stack gap={0.5}>
      <Autocomplete
        multiple
        freeSolo
        disabled={disabled}
        // Ids, not names: the option list is the set of storable values, and MUI puts the selected
        // option straight into `value`. Offering names is what stored "Apple TV" beside the existing
        // `APPLE_TV` — one device, two entries, and the readable one was the invalid one.
        options={(options ?? []).map((option) => option.id)}
        getOptionLabel={(option) =>
          typeof option === 'string'
            ? vocabularyMatch(option)?.name ?? option
            : option
        }
        value={value}
        onChange={(_, next) => onChange(name, dedupeTags(next as string[]))}
        renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => {
            // MUI supplies `key` inside these props; spreading it is how it wants to be used, but
            // React then warns unless the key is also passed explicitly.
            const { key, ...tagProps } = getTagProps({ index });
            const curated = vocabularyMatch(tag);
            return (
              <Chip
                key={key}
                size="small"
                // Show the curated spelling when the value is recognised, the raw value when it is not
                // — an unrecognised value has to stay legible to be fixable.
                label={labelFor ? labelFor(tag) : curated?.name ?? tag}
                {...tagProps}
                // Amber only for a value the vocabulary genuinely does not contain, matched the same
                // way validation matches it.
                color={options?.length && !curated ? 'warning' : 'default'}
                variant="outlined"
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            id={`field-${name}`}
            className={
              worst === ValidationLevel.Warning ? 'warning' : undefined
            }
            label={label}
            error={!!worst}
            helperText={helper}
            placeholder="Add…"
          />
        )}
      />
      {hasOverride && !original?.length && (
        <Typography variant="caption" color="text.secondary">
          Provider sent nothing for this field.
        </Typography>
      )}
    </Stack>
  );
}
